import { useLanguage } from '../contexts/LanguageContext';
import { Rocket, Zap, Send, CreditCard, Code, Bot, Mail, Clock, ChevronRight } from 'lucide-react';
import SEO from '../components/SEO';
import LocaleLink from '../components/LocaleLink';

const iconMap = { Rocket, Zap, Send, CreditCard, Code, Bot };

const content = {
  ar: {
    badge: 'مركز الدعم',
    h1: 'كيف يمكننا المساعدة؟',
    sub: 'اعثر على إجابات وأدلة وتواصل مع فريق الدعم.',
    search: 'ابحث في وثائق الدعم...',
    categories: [
      { icon: 'Rocket', title: 'البدء السريع', desc: 'إعداد حسابك والبدء في استخدام منتجات كويدوري.' },
      { icon: 'Zap', title: 'QuidPulse', desc: 'إعداد خلاصة الذكاء والتصفية والتخصيص.' },
      { icon: 'Send', title: 'النشر', desc: 'إعداد نشر المنشورات والفيديو والأتمتة.' },
      { icon: 'CreditCard', title: 'الحساب والفواتير', desc: 'إدارة اشتراكك وفواتيرك وإعدادات الحساب.' },
      { icon: 'Code', title: 'الواجهة البرمجية والتكاملات', desc: 'الوثائق التقنية وأدلة التكامل.' },
      { icon: 'Bot', title: 'الوكلاء الذكيون', desc: 'تكوين ونشر وكلاء الذكاء الاصطناعي المحادثيين.' },
    ],
    faq: {
      h: 'أسئلة شائعة',
      items: [
        { q: 'كيف أبدأ مع كويدوري؟', a: 'أنشئ حساباً مجانياً وستتمكن فوراً من الوصول إلى QuidPulse. يمكنك الترقية في أي وقت لمزيد من الإمكانيات.' },
        { q: 'ما اللغات المدعومة؟', a: 'تدعم المنصة العربية والإنجليزية حالياً. المزيد من اللغات قادم مستقبلاً.' },
        { q: 'كيف يعمل نشر المحتوى تلقائياً؟', a: 'بعد الإعداد، يقوم الذكاء الاصطناعي بإنشاء المحتوى وجدولته ونشره على القنوات المختارة حسب إعداداتك.' },
        { q: 'هل يمكنني إلغاء اشتراكي؟', a: 'نعم، يمكنك إلغاء اشتراكك في أي وقت من إعدادات حسابك. لن يتم محاسبتك بعد الإلغاء.' },
      ],
    },
    contact: { h: 'لا تزال بحاجة إلى مساعدة؟', sub: 'فريق الدعم متاح للمساعدة.', email: 'support@quidory.com', time: 'رد خلال ٢٤ ساعة' },
  },
  en: {
    badge: 'Support Center',
    h1: 'How Can We Help?',
    sub: 'Find answers, guides, and contact our support team.',
    search: 'Search documentation...',
    categories: [
      { icon: 'Rocket', title: 'Getting Started', desc: 'Set up your account and start using Quidory products.' },
      { icon: 'Zap', title: 'QuidPulse', desc: 'Intelligence feed setup, filtering, and customization.' },
      { icon: 'Send', title: 'Publishing', desc: 'Post and video publishing setup and automation.' },
      { icon: 'CreditCard', title: 'Account & Billing', desc: 'Manage your subscription, billing, and account settings.' },
      { icon: 'Code', title: 'API & Integrations', desc: 'Technical documentation and integration guides.' },
      { icon: 'Bot', title: 'AI Agents', desc: 'Configure and deploy conversational AI agents.' },
    ],
    faq: {
      h: 'Frequently Asked Questions',
      items: [
        { q: 'How do I get started with Quidory?', a: 'Create a free account and you will have immediate access to QuidPulse. You can upgrade at any time for more capabilities.' },
        { q: 'What languages are supported?', a: 'The platform currently supports Arabic and English. More languages are coming in the future.' },
        { q: 'How does automated publishing work?', a: 'After setup, AI generates content, schedules it, and publishes to your selected channels according to your configured settings.' },
        { q: 'Can I cancel my subscription?', a: 'Yes, you can cancel your subscription at any time from your account settings. You will not be charged after cancellation.' },
      ],
    },
    contact: { h: 'Still Need Help?', sub: 'Our support team is available to assist you.', email: 'support@quidory.com', time: 'Response within 24 hours' },
  },
};

const SupportPage = () => {
  const { language } = useLanguage();
  const c = content[language];

  return (
    <div className="pt-16">
      <SEO
        titleEn="Support Center | Quidory"
        titleAr="مركز الدعم | كويدوري"
        descEn="Get help with Quidory products. Find documentation, guides, and contact our support team."
        descAr="احصل على المساعدة مع منتجات كويدوري. اعثر على الوثائق والأدلة وتواصل مع فريق الدعم."
        path="/support"
      />

      {/* Hero */}
      <section className="relative py-20 bg-background overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(212,175,55,0.06)_0%,transparent_60%)]" />
        <div className="relative max-w-2xl mx-auto px-4 text-center">
          <span className="text-xs font-semibold uppercase tracking-widest text-gold mb-4 block">{c.badge}</span>
          <h1 className="text-4xl sm:text-5xl font-light tracking-tight text-foreground mb-4">{c.h1}</h1>
          <p className="text-lg text-muted-foreground mb-8">{c.sub}</p>
          <div className="relative max-w-md mx-auto">
            <input
              data-testid="support-search"
              type="text"
              placeholder={c.search}
              className="w-full px-5 py-3.5 pe-12 rounded-xl border border-border bg-card text-foreground placeholder:text-muted-foreground/60 text-sm focus:outline-none focus:ring-2 focus:ring-gold/30 focus:border-gold/40 transition-colors"
            />
            <Mail className="absolute end-4 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          </div>
        </div>
      </section>

      {/* Categories */}
      <section className="py-16 bg-card border-y border-border">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {c.categories.map((cat, i) => {
              const Icon = iconMap[cat.icon];
              return (
                <button
                  key={i}
                  data-testid={`support-category-${i}`}
                  className="group flex items-start gap-4 p-5 rounded-xl border border-border bg-background text-start hover:border-gold/30 hover:bg-card transition-all duration-200"
                >
                  <div className="p-2.5 rounded-lg bg-gold/10 shrink-0 group-hover:bg-gold/15 transition-colors">
                    <Icon className="h-4 w-4 text-gold" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-sm font-semibold text-foreground mb-1">{cat.title}</h3>
                    <p className="text-xs text-muted-foreground leading-relaxed">{cat.desc}</p>
                  </div>
                  <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0 mt-0.5 opacity-0 group-hover:opacity-100 transition-opacity" />
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-background">
        <div className="max-w-2xl mx-auto px-4 sm:px-6">
          <h2 className="text-2xl font-light text-foreground mb-8 text-center">{c.faq.h}</h2>
          <div className="space-y-3">
            {c.faq.items.map((item, i) => (
              <details
                key={i}
                data-testid={`faq-detail-${i}`}
                className="group border border-border rounded-xl overflow-hidden"
              >
                <summary className="flex items-center justify-between px-5 py-4 text-sm font-medium text-foreground cursor-pointer hover:bg-muted/50 transition-colors list-none">
                  {item.q}
                  <ChevronRight className="h-4 w-4 text-muted-foreground transition-transform group-open:rotate-90 shrink-0 ms-3" />
                </summary>
                <div className="px-5 pb-4 pt-2 text-sm text-muted-foreground leading-relaxed border-t border-border">
                  {item.a}
                </div>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="py-16 bg-card border-t border-border">
        <div className="max-w-md mx-auto px-4 text-center">
          <h2 className="text-2xl font-light text-foreground mb-2">{c.contact.h}</h2>
          <p className="text-muted-foreground mb-6 text-sm">{c.contact.sub}</p>
          <div className="space-y-3">
            <div className="flex items-center gap-2 justify-center">
              <Mail className="h-4 w-4 text-gold" />
              <a href="mailto:support@quidory.com" className="text-sm font-medium text-gold hover:text-gold/80 transition-colors">
                {c.contact.email}
              </a>
            </div>
            <div className="flex items-center gap-2 justify-center">
              <Clock className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm text-muted-foreground">{c.contact.time}</span>
            </div>
          </div>
          <LocaleLink
            to="/contact"
            data-testid="support-contact-btn"
            className="mt-6 inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-foreground text-background text-sm font-medium hover:bg-foreground/90 transition-colors"
          >
            {language === 'ar' ? 'فتح تذكرة دعم' : 'Open Support Ticket'}
          </LocaleLink>
          <p className="mt-8 text-xs text-muted-foreground/60 leading-relaxed">
            {language === 'ar'
              ? 'يُشغَّل كويدوري بواسطة Al Yakhour LLC، وايومنغ، الولايات المتحدة.'
              : 'Quidory is operated by Al Yakhour LLC · Wyoming, USA.'}
          </p>
        </div>
      </section>
    </div>
  );
};

export default SupportPage;
